import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { cardStyle, accent, accentLight, textPrimary, statValueStyle, bgMuted } from './styles'
import EventCard from './EventCard'
import { generateShareText, isSunday, DAILY_CREDIT, DAY_MULTIPLIER } from './gameLogic'

function RevealScreen({ anchor, placedEvents, strikes, accuracy, wager, weeklyBank, onComplete, onRevealComplete }) {
  const navigate = useNavigate()
  const [revealIndex, setRevealIndex] = useState(-1)
  const [showScore, setShowScore] = useState(false)
  const savedRef = useRef(false)

  useEffect(() => {
    // Auto-advance reveals
    if (revealIndex < placedEvents.length) {
      const timer = setTimeout(() => {
        setRevealIndex(revealIndex + 1)
      }, 500)
      return () => clearTimeout(timer)
    } else if (revealIndex === placedEvents.length) {
      // Reveal complete after showing all cards - show score
      const timer = setTimeout(() => {
        setShowScore(true)
      }, 800)
      return () => clearTimeout(timer)
    }
  }, [revealIndex, placedEvents.length])

  // Save result when score is shown (only once)
  useEffect(() => {
    if (showScore && onRevealComplete && !savedRef.current) {
      savedRef.current = true
      onRevealComplete()
    }
  }, [showScore, onRevealComplete])

  const isSundayGame = isSunday()
  const scoreMultipliers = { 4: 1, 3: 0.5, 2: 0, 1: -0.5, 0: -1 }
  const scoreMultiplier = scoreMultipliers[accuracy]
  const dayMultiplier = isSundayGame ? DAY_MULTIPLIER.sunday : DAY_MULTIPLIER.weekday
  const payout = wager * scoreMultiplier * dayMultiplier
  const bankedToday = DAILY_CREDIT + payout
  const shareText = generateShareText(new Date().getDate(), bankedToday, strikes, weeklyBank + bankedToday)
  const [copied, setCopied] = useState(false)

  const handleCopyShare = () => {
    navigator.clipboard.writeText(shareText)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: bgMuted, padding: '20px 16px 100px' }}>
      <div style={{ maxWidth: '500px', margin: '0 auto' }}>
        {/* Title */}
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, fontSize: '1.3rem', color: textPrimary }}>Revealing Years...</h2>
        </div>

        {/* Timeline */}
        <div style={{ ...cardStyle, marginBottom: showScore ? '20px' : '20px' }}>
          {(() => {
            // Combine anchor and placed events, sort chronologically (newest at top)
            const allEvents = [anchor, ...placedEvents].sort((a, b) => b.year - a.year)

            return allEvents.map((event, idx) => {
              const eventIndex = placedEvents.findIndex(e => e.id === event.id) + 1
              const isRevealed = event.id === anchor.id || revealIndex >= eventIndex

              return (
                <div key={event.id}>
                  {isRevealed ? (
                    <EventCard
                      event={event}
                      isAnchor={event.id === anchor.id}
                      showYear={true}
                      wasStrike={event.wasStrike}
                      revealAnimating={true}
                    />
                  ) : (
                    <div style={{ padding: '12px', backgroundColor: '#f0f0f0', borderRadius: '12px', marginBottom: '12px', textAlign: 'center', color: '#999' }}>
                      Waiting...
                    </div>
                  )}
                </div>
              )
            })
          })()}
        </div>

        {/* Score Summary - shows after reveal completes */}
        {showScore && (
          <>
            {/* Score Card */}
            <div style={{ ...cardStyle }}>
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <div style={{ fontSize: '0.7rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.07em', color: '#aaa', marginBottom: '8px' }}>
                  Today's Score
                </div>
                <div style={{ fontSize: '3rem', fontWeight: '900', color: accuracy === 4 ? accent : '#666' }}>
                  {accuracy}/4
                </div>
              </div>

              {/* Strike grid */}
              <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '20px' }}>
                {strikes.map((isStrike, idx) => (
                  <div
                    key={idx}
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '8px',
                      backgroundColor: isStrike ? '#fef2f2' : '#f0fdf4',
                      border: `2px solid ${isStrike ? '#dc2626' : '#16a34a'}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.5rem',
                      fontWeight: '900',
                    }}
                  >
                    {isStrike ? '🔴' : '🟢'}
                  </div>
                ))}
              </div>

              {/* Sunday 2x Multiplier Badge */}
              {isSundayGame && (
                <div style={{ padding: '12px 16px', backgroundColor: '#fff7ed', borderRadius: '12px', border: `2px solid ${accent}`, marginBottom: '20px', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: '700', color: accent, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>
                    ⚡ 2x Sunday Multiplier Applied
                  </div>
                  <div style={{ fontSize: '0.7rem', color: '#666' }}>
                    All payouts and losses doubled today
                  </div>
                </div>
              )}

              {/* Payout summary */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                <div style={{
                  padding: '16px',
                  backgroundColor: payout > 0 ? '#f0fdf4' : payout < 0 ? '#fef2f2' : accentLight,
                  borderRadius: '12px',
                  borderLeft: `4px solid ${payout > 0 ? '#15803d' : payout < 0 ? '#dc2626' : accent}`
                }}>
                  <div style={{ fontSize: '0.7rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#999', marginBottom: '4px' }}>
                    Payout
                  </div>
                  <div style={{ ...statValueStyle, color: payout > 0 ? '#15803d' : payout < 0 ? '#dc2626' : accent }}>
                    {payout > 0 ? '+' : ''}{payout.toFixed(2)}
                  </div>
                </div>

                <div style={{ padding: '16px', backgroundColor: bankedToday > 0 ? '#f0fdf4' : '#fef2f2', borderRadius: '12px' }}>
                  <div style={{ fontSize: '0.7rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#999', marginBottom: '4px' }}>
                    Banked Today
                  </div>
                  <div style={{ ...statValueStyle, color: bankedToday > 0 ? '#15803d' : '#dc2626' }}>${bankedToday.toFixed(2)}</div>
                </div>
              </div>

            </div>

            {/* Buttons */}
            <button
              onClick={() => navigate('/leaderboard')}
              style={{
                width: '100%',
                padding: '13px',
                fontSize: '1rem',
                fontWeight: 'bold',
                backgroundColor: accent,
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                boxShadow: `0 2px 0 #c2410c`,
                marginBottom: '12px',
              }}
            >
              🏆 View Leaderboard
            </button>

              {/* Share section */}
              <div style={{ padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '12px', marginBottom: '16px' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#999', marginBottom: '8px' }}>
                  Share With Friends
                </div>
                <pre
                  style={{
                    margin: '8px 0',
                    padding: '12px',
                    backgroundColor: '#ffffff',
                    borderRadius: '8px',
                    fontSize: '0.8rem',
                    fontFamily: 'monospace',
                    lineHeight: '1.4',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {shareText}
                </pre>
                <button
                  onClick={handleCopyShare}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#f0f0f0',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    color: textPrimary,
                    transition: 'all 0.15s ease',
                  }}
                >
                  {copied ? '✓ Copied!' : 'Copy to Clipboard'}
                </button>
              </div>

            <button
              onClick={onComplete}
              style={{
                width: '100%',
                padding: '13px',
                fontSize: '1rem',
                fontWeight: 'bold',
                backgroundColor: '#f0f0f0',
                color: textPrimary,
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                cursor: 'pointer',
              }}
            >
              Back Home
            </button>
          </>
        )}
      </div>
    </div>
  )
}

export default RevealScreen
